import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  X,
  Send,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
  MessageCircleQuestion,
} from 'lucide-react';
import api from '../../lib/api';
import { useHelpContext } from '../../lib/helpContext';
import { highlightOrNavigate } from '../../lib/helpHighlight';
import { track } from '../../lib/analytics';

/** After this much inactivity a fresh conversation (new sessionId, empty transcript) starts. */
const IDLE_RESET_MS = 30 * 60 * 1000;

function newSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  const rand = Math.random().toString(36).slice(2, 12);
  return `s-${Date.now().toString(36)}-${rand}`;
}

interface DeepLink {
  label: string;
  url: string;
}

interface ChatResponse {
  id: string | null;
  answer: string;
  deepLinks: DeepLink[];
  highlightElement: string | null;
  sources: string[];
  meta?: { provider?: string; embeddingUsed?: boolean };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  id?: string | null;
  deepLinks?: DeepLink[];
  highlightElement?: string | null;
  sources?: string[];
  feedback?: 'up' | 'down';
}

const SUGGESTIONS: Record<string, string[]> = {
  AGENCY: [
    'How do I create a campaign?',
    'Where do I invite KOLs?',
    'How do I generate a PDF report?',
  ],
  BRAND: [
    'How do I approve a submission?',
    'Where do I review scripts?',
    'How do I approve the shortlist?',
  ],
  KOL: [
    'How do I request changes to an invitation?',
    'How do I submit content?',
    'Where do I sign my contract?',
  ],
  ADMIN: ['How do I grant AI credits?', 'Where do I manage users?'],
};

export default function HelpChatWidget() {
  const ctx = useHelpContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  const lastActivityRef = useRef(0);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  if (!ctx) return null;
  const themeClass = `theme-${ctx.workspaceType === 'unknown' ? 'agency' : ctx.workspaceType}`;
  const suggestions = SUGGESTIONS[ctx.role] ?? [];

  /** Reuse the session while active; after IDLE_RESET_MS start a new one and clear the transcript. */
  function ensureSession(): string {
    const now = Date.now();
    const expired = !sessionIdRef.current || now - lastActivityRef.current > IDLE_RESET_MS;
    if (expired) {
      sessionIdRef.current = newSessionId();
      setMessages([]);
    }
    lastActivityRef.current = now;
    return sessionIdRef.current as string;
  }

  async function ask(question: string) {
    const q = question.trim();
    if (!q || loading || !ctx) return;
    const sessionId = ensureSession();
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setLoading(true);
    try {
      const res = await api.post<ChatResponse>('/help/chat', {
        question: q,
        currentRoute: ctx.currentRoute,
        currentModule: ctx.currentModule,
        entity: ctx.entity,
        sessionId,
      });
      const d = res.data;
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: d.answer,
          id: d.id,
          deepLinks: d.deepLinks ?? [],
          highlightElement: d.highlightElement,
          sources: d.sources ?? [],
        },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: 'Sorry, the help assistant is unavailable right now. Please try again, or contact i@kol.my.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void ask(input);
  }

  function showMe(msg: ChatMessage) {
    const fallback = msg.deepLinks && msg.deepLinks[0] ? msg.deepLinks[0].url : null;
    const ok = highlightOrNavigate(msg.highlightElement, fallback, (url) => {
      navigate(url);
      setOpen(false);
    });
    if (ok && msg.highlightElement) setOpen(false);
  }

  async function sendFeedback(idx: number, value: 'up' | 'down') {
    const msg = messages[idx];
    if (!msg?.id) return;
    setMessages((m) => m.map((x, i) => (i === idx ? { ...x, feedback: value } : x)));
    track('help_chat_feedback', { feedback: value, module: ctx?.currentModule ?? null });
    try {
      await api.post('/help/feedback', { id: msg.id, feedback: value });
    } catch {
      /* non-blocking */
    }
  }

  return (
    <div className={themeClass}>
      {/* Floating action button */}
      {!open && (
        <button
          type="button"
          onClick={() => {
            ensureSession();
            track('help_widget_opened', { module: ctx.currentModule, role: ctx.role });
            setOpen(true);
          }}
          aria-label="Open help assistant"
          className="fixed bottom-6 right-6 z-[60] flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg shadow-black/20 transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent2/50"
          style={{ backgroundColor: 'rgb(var(--accent-rgb))' }}
        >
          <MessageCircleQuestion className="h-6 w-6" aria-hidden />
        </button>
      )}

      {/* Slide-in panel */}
      {open && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="relative flex h-full w-full max-w-md flex-col glass-card !rounded-none animate-slide-in-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Help assistant"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-glass bg-accent2/15 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg border border-accent2/30 bg-accent2/20 p-2">
                  <Sparkles className="h-5 w-5 text-accent2" aria-hidden />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-glass-text">myKOL Assistant</h2>
                  <p className="text-xs text-glass-text-3">Ask how to do anything in myKOL</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-glass-text-3 transition hover:bg-glass-3 hover:text-glass-text"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto glass-scroll p-4">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-glass bg-glass-2 p-4 text-sm text-glass-text-2">
                    Hi! I can help you find features and walk you through tasks in myKOL. Ask me
                    things like “where do I…?” or “how do I…?”.
                  </div>
                  {suggestions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium uppercase tracking-wide text-glass-text-3">
                        Try asking
                      </p>
                      {suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => void ask(s)}
                          className="block w-full rounded-lg border border-glass bg-glass-1 px-3 py-2 text-left text-sm text-glass-text transition hover:border-accent2/50 hover:bg-glass-3"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {messages.map((m, i) =>
                m.role === 'user' ? (
                  <div key={i} className="flex justify-end">
                    <div
                      className="max-w-[85%] rounded-2xl rounded-br-sm px-4 py-2 text-sm text-white"
                      style={{ backgroundColor: 'rgb(var(--accent-rgb))' }}
                    >
                      {m.text}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex justify-start">
                    <div className="max-w-[90%] space-y-3">
                      <div className="whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-glass bg-glass-2 px-4 py-3 text-sm text-glass-text">
                        {m.text}
                      </div>

                      {m.highlightElement && (
                        <button
                          type="button"
                          onClick={() => showMe(m)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-accent2/40 bg-accent2/10 px-3 py-1.5 text-xs font-medium text-accent2 transition hover:bg-accent2/20"
                        >
                          <Sparkles className="h-3.5 w-3.5" aria-hidden />
                          Show me
                        </button>
                      )}

                      {m.deepLinks && m.deepLinks.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {m.deepLinks.map((l) => (
                            <button
                              key={l.url}
                              type="button"
                              onClick={() => {
                                navigate(l.url);
                                setOpen(false);
                              }}
                              className="inline-flex items-center gap-1 rounded-lg border border-glass bg-glass-1 px-3 py-1.5 text-xs font-medium text-glass-text transition hover:border-accent2/50 hover:bg-glass-3"
                            >
                              {l.label}
                              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-3 pl-1">
                        {m.id && (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => void sendFeedback(i, 'up')}
                              aria-label="Helpful"
                              className={`rounded p-1 transition hover:bg-glass-3 ${
                                m.feedback === 'up' ? 'text-emerald-500' : 'text-glass-text-3'
                              }`}
                            >
                              <ThumbsUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => void sendFeedback(i, 'down')}
                              aria-label="Not helpful"
                              className={`rounded p-1 transition hover:bg-glass-3 ${
                                m.feedback === 'down' ? 'text-rose-500' : 'text-glass-text-3'
                              }`}
                            >
                              <ThumbsDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        {m.sources && m.sources.length > 0 && (
                          <span className="text-[10px] text-glass-text-3">
                            Sources: {m.sources.length}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}

              {loading && (
                <div className="flex items-center gap-2 text-sm text-glass-text-3">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Thinking…
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={onSubmit} className="border-t border-glass bg-glass-2 p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void ask(input);
                    }
                  }}
                  rows={1}
                  placeholder="Ask the myKOL Assistant…"
                  className="max-h-28 min-h-[40px] flex-1 resize-none rounded-lg border border-glass bg-glass-1 px-3 py-2 text-sm text-glass-text focus:outline-none focus:ring-2 focus:ring-accent2/40"
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  aria-label="Send"
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-white transition disabled:opacity-40"
                  style={{ backgroundColor: 'rgb(var(--accent-rgb))' }}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
